import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CarouselProvider, Slider, Slide, ButtonBack, ButtonNext } from 'pure-react-carousel';
import { Box } from '@mui/material';
import { ArrowLeftIconSvg, ArrowRightIconSvg } from '../../../../icons';
import { CarouselDotGroup } from './dotgroup';

const BUTTON_STYLES = {
  backgroundColor: 'transparent',
  border: 'none',
  padding: 0
};

const CAROUSEL_ANIMATION = {
  transition: 'transform .5s ease-in-out .2s'
};

type Props = {
  items: Array<JSX.Element>;
  maxVisibleSlides?: number;
  leftRightButtonSize?: number;
  minimumItemWidth?: number;
  itemGap?: number;
  parent?: HTMLDivElement;
  isPlaying?: boolean;
  interval?: number;
};

/**
 * Set your desired minimum width for one visible carousel item
 * on the component prop minimumItemWidth (default: 200px).
 * The number of visible items will be calculated based on this value,
 * the available space and desired maximum number of items.
 */

export const Carousel: FC<Props> = ({
  items,
  maxVisibleSlides = 4,
  leftRightButtonSize = 48,
  minimumItemWidth = 200,
  itemGap = 16,
  isPlaying,
  interval
}) => {
  const wrapperBoxRef = useRef<HTMLDivElement>(null);
  const [wrapperBox, setWrapperBox] = useState<HTMLDivElement>();
  const [visibleSlides, setVisibleSlides] = useState<number>(() => maxVisibleSlides);

  const calcVisibleItems = () => {
    if (wrapperBox) {
      const availableSpace = wrapperBox.clientWidth;
      const neededSpace = maxVisibleSlides * (minimumItemWidth + itemGap);

      if (availableSpace < neededSpace) {
        const numberOfVisibleItems = Math.floor(availableSpace / (minimumItemWidth + itemGap));

        setVisibleSlides(numberOfVisibleItems);
        return;
      }

      setVisibleSlides(maxVisibleSlides);
    }
  };

  useLayoutEffect(() => {
    let isCancelled = false;
    const setup = async () => {
      if (isCancelled) {
        return;
      }

      const wrapper = document.querySelector<HTMLDivElement>('#carousel-slider');
      if (wrapper) {
        setWrapperBox(wrapper);
      } else {
        setup();
      }
    };

    setup();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (wrapperBox) {
      calcVisibleItems();
      window.addEventListener('resize', calcVisibleItems);
    }

    return () => window.removeEventListener('resize', calcVisibleItems);
  }, [wrapperBox]);

  /**
   * This Component is based on the package Pure React Carousel.
   * For further Customization see:
   * https://www.npmjs.com/package/pure-react-carousel
   */

  return (
    <CarouselProvider
      totalSlides={items.length}
      naturalSlideHeight={0}
      naturalSlideWidth={1}
      isIntrinsicHeight={true}
      visibleSlides={visibleSlides}
      isPlaying={isPlaying}
      interval={interval}
    >
      <Box
        ref={wrapperBoxRef}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'min-content auto min-content',
          '& .carousel__dot-group': {
            gridColumn: '1 / span 3'
          },
          alignItems: 'center',
          '& > button:hover:enabled': {
            color: 'grey.900'
          },
          '& .carousel-animation': CAROUSEL_ANIMATION,
          '& .carousel-tray': {
            gap: itemGap + 'px'
          }
        }}
      >
        <ButtonBack style={BUTTON_STYLES}>
          <ArrowLeftIconSvg width={leftRightButtonSize} height={leftRightButtonSize} />
        </ButtonBack>
        <Slider
          id="carousel-slider"
          classNameAnimation="carousel-animation"
          classNameTray="carousel-tray"
          style={{
            overflowX: 'hidden'
          }}
        >
          {items.map((item, index) => (
            <Slide key={index} index={index}>
              {item}
            </Slide>
          ))}
        </Slider>
        <ButtonNext style={BUTTON_STYLES}>
          <ArrowRightIconSvg width={leftRightButtonSize} height={leftRightButtonSize} />
        </ButtonNext>
        <CarouselDotGroup />
      </Box>
    </CarouselProvider>
  );
};
